"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyEventDetailVariablesIfPresent = void 0;
const waypointBlocks_1 = require("./waypointBlocks");
/** Separator paragraph rendered between the event-detail lines in the invitation / reminder templates. */
const EVENT_DETAIL_SEPARATOR = '<p style="line-height:1.2;text-align:center;line-height:1.2"><br></p>';
/** Matches one event-detail line, keyed by its `data-event-field` marker. */
const EVENT_DETAIL_LINE_REGEX = /<p[^>]*\bdata-event-field="(location|date|time)"[^>]*>.*?<\/p>/g;
const EVENT_DETAIL_MARKER_REGEX = /\s*data-event-field="[^"]*"/g;
/** Reads the block's contents string when it has one (Html/Heading blocks); undefined otherwise. */
const getBlockContents = (block) => {
    const contents = block?.data?.props?.contents;
    return typeof contents === "string" ? contents : undefined;
};
/**
 * In an event-details block, keeps only the lines whose value is present (location/date/time) and drops the
 * rest, then strips the `data-event-field` markers (they exist only to drive this pass). Called only when at
 * least one detail is present; the all-absent case removes the whole block via `removeEventDetailBlocks`.
 */
const stripAbsentEventDetailLines = (contents, present) => {
    const lineRegex = new RegExp(EVENT_DETAIL_LINE_REGEX.source, "g");
    const matches = [];
    let match;
    while ((match = lineRegex.exec(contents)) !== null) {
        matches.push({ field: match[1], text: match[0], index: match.index });
    }
    if (matches.length === 0) {
        return contents;
    }
    const keptLines = matches
        .filter((line) => present[line.field])
        .map((line) => line.text.replace(EVENT_DETAIL_MARKER_REGEX, ""));
    const first = matches[0];
    const last = matches[matches.length - 1];
    const end = last.index + last.text.length;
    return contents.slice(0, first.index) + keptLines.join(EVENT_DETAIL_SEPARATOR) + contents.slice(end);
};
/**
 * Removes the event-details block(s) (their contents carry the `data-event-field` markers) together with any
 * container whose only child is that block, then cleans the removed ids from every remaining childrenIds
 * array. Used when the form has no location/date/time at all — an empty details box would look broken.
 */
const removeEventDetailBlocks = (template) => {
    const eventBlockIds = Object.entries(template)
        .filter(([, block]) => getBlockContents(block)?.includes("data-event-field"))
        .map(([id]) => id);
    if (eventBlockIds.length === 0) {
        return;
    }
    const idsToRemove = new Set();
    for (const eventId of eventBlockIds) {
        // Delete the whole dedicated wrapper container when the event block is its only child; else just the block.
        const wrapper = Object.entries(template).find(([, block]) => {
            const children = (0, waypointBlocks_1.getAllChildrenIds)(block);
            return children.length === 1 && children[0] === eventId;
        });
        const removalRootId = wrapper?.[0] ?? eventId;
        for (const id of (0, waypointBlocks_1.getAllBlockIdsToDelete)(removalRootId, template)) {
            idsToRemove.add(id);
        }
    }
    for (const id of idsToRemove) {
        delete template[id];
    }
    for (const [id, block] of Object.entries(template)) {
        template[id] = (0, waypointBlocks_1.cleanRemovedChildrenIds)(block, idsToRemove);
    }
};
/**
 * Reconciles the event-details block with the form's event data, mutating `template` in place:
 * - when at least one of location/date/time is present, keeps the present line(s) and drops the absent ones;
 * - when none are present, removes the event-details block and its dedicated container entirely.
 */
const applyEventDetailVariablesIfPresent = (template, present) => {
    if (present.location || present.date || present.time) {
        for (const block of Object.values(template)) {
            const contents = getBlockContents(block);
            if (contents?.includes("data-event-field")) {
                block.data.props.contents = stripAbsentEventDetailLines(contents, present);
            }
        }
    }
    else {
        removeEventDetailBlocks(template);
    }
};
exports.applyEventDetailVariablesIfPresent = applyEventDetailVariablesIfPresent;
